/**
 * Fast, standalone 3D Vector class with zero external dependencies.
 * Fully interoperable with Three.js Vector3 and plain {x, y, z} objects.
 */
export class Vec3 {
  constructor(x = 0, y = 0, z = 0) {
    if (typeof x === 'object' && x !== null) {
      this.x = Number(x.x) || 0;
      this.y = Number(x.y) || 0;
      this.z = Number(x.z) || 0;
    } else {
      this.x = Number(x) || 0;
      this.y = Number(y) || 0;
      this.z = Number(z) || 0;
    }
  }

  set(x, y, z) {
    if (typeof x === 'object' && x !== null) {
      this.x = Number(x.x) || 0;
      this.y = Number(x.y) || 0;
      this.z = Number(x.z) || 0;
    } else {
      this.x = Number(x) || 0;
      this.y = Number(y) || 0;
      this.z = Number(z) || 0;
    }
    return this;
  }

  copy(v) {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }

  clone() {
    return new Vec3(this.x, this.y, this.z);
  }

  add(v) {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }

  addVectors(a, b) {
    this.x = a.x + b.x;
    this.y = a.y + b.y;
    this.z = a.z + b.z;
    return this;
  }

  addScaledVector(v, s) {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    return this;
  }

  sub(v) {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }

  subVectors(a, b) {
    this.x = a.x - b.x;
    this.y = a.y - b.y;
    this.z = a.z - b.z;
    return this;
  }

  multiplyScalar(s) {
    this.x *= s;
    this.y *= s;
    this.z *= s;
    return this;
  }

  divideScalar(s) {
    if (s !== 0) {
      const inv = 1 / s;
      this.x *= inv;
      this.y *= inv;
      this.z *= inv;
    } else {
      this.x = 0;
      this.y = 0;
      this.z = 0;
    }
    return this;
  }

  dot(v) {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  cross(v) {
    return this.crossVectors(this, v);
  }

  crossVectors(a, b) {
    const ax = a.x, ay = a.y, az = a.z;
    const bx = b.x, by = b.y, bz = b.z;
    this.x = ay * bz - az * by;
    this.y = az * bx - ax * bz;
    this.z = ax * by - ay * bx;
    return this;
  }

  lengthSq() {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  length() {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  distanceToSq(v) {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    const dz = this.z - v.z;
    return dx * dx + dy * dy + dz * dz;
  }

  distanceTo(v) {
    return Math.sqrt(this.distanceToSq(v));
  }

  normalize() {
    const len = this.length();
    if (len > 1e-9) {
      return this.multiplyScalar(1 / len);
    }
    this.x = 0;
    this.y = 0;
    this.z = 0;
    return this;
  }

  negate() {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    return this;
  }

  min(v) {
    this.x = Math.min(this.x, v.x);
    this.y = Math.min(this.y, v.y);
    this.z = Math.min(this.z, v.z);
    return this;
  }

  max(v) {
    this.x = Math.max(this.x, v.x);
    this.y = Math.max(this.y, v.y);
    this.z = Math.max(this.z, v.z);
    return this;
  }

  clamp(min, max) {
    this.x = Math.max(min.x, Math.min(max.x, this.x));
    this.y = Math.max(min.y, Math.min(max.y, this.y));
    this.z = Math.max(min.z, Math.min(max.z, this.z));
    return this;
  }

  clampScalar(minVal, maxVal) {
    this.x = Math.max(minVal, Math.min(maxVal, this.x));
    this.y = Math.max(minVal, Math.min(maxVal, this.y));
    this.z = Math.max(minVal, Math.min(maxVal, this.z));
    return this;
  }

  lerp(v, alpha) {
    this.x += (v.x - this.x) * alpha;
    this.y += (v.y - this.y) * alpha;
    this.z += (v.z - this.z) * alpha;
    return this;
  }

  applyAxisAngle(axis, angle) {
    // Rodrigues rotation formula
    const halfAngle = angle * 0.5;
    const s = Math.sin(halfAngle);
    const qx = axis.x * s;
    const qy = axis.y * s;
    const qz = axis.z * s;
    const qw = Math.cos(halfAngle);

    const ix = qw * this.x + qy * this.z - qz * this.y;
    const iy = qw * this.y + qz * this.x - qx * this.z;
    const iz = qw * this.z + qx * this.y - qy * this.x;
    const iw = -qx * this.x - qy * this.y - qz * this.z;

    this.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
    this.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
    this.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;
    return this;
  }

  rotateY(angle) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const x = this.x * cos + this.z * sin;
    const z = -this.x * sin + this.z * cos;
    this.x = x;
    this.z = z;
    return this;
  }

  equals(v, epsilon = 1e-6) {
    return (
      Math.abs(this.x - v.x) <= epsilon &&
      Math.abs(this.y - v.y) <= epsilon &&
      Math.abs(this.z - v.z) <= epsilon
    );
  }

  toArray() {
    return [this.x, this.y, this.z];
  }

  static from(v) {
    if (v instanceof Vec3) return v.clone();
    if (Array.isArray(v)) return new Vec3(v[0], v[1], v[2]);
    if (typeof v === 'object' && v !== null) return new Vec3(v.x, v.y, v.z);
    return new Vec3();
  }

  static zero() {
    return new Vec3(0, 0, 0);
  }

  static up() {
    return new Vec3(0, 1, 0);
  }
}
